import { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const UNITS = ["pcs", "kg", "litre", "ml"];

const formatBDT = (value) => {
  const amount = Number(value) || 0;
  return `BDT ${amount.toLocaleString("en-BD", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const today = () => new Date().toISOString().split("T")[0];

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  outline: "none",
  fontSize: 13,
  color: "#0f172a",
  background: "#ffffff",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#475569",
  marginBottom: 6,
};

const emptyForm = {
  productName: "",
  nameSideText: "",
  category: "",
  quantity: "",
  unit: "pcs",
  entryDate: today(),
  purchaseRate: "",
  sellingRate: "",
};

export default function MBDSPage() {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "mbdsProducts"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });

    return unsubscribe;
  }, []);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;

    return products.filter((product) =>
      product.productName?.toLowerCase().includes(term) ||
      product.nameSideText?.toLowerCase().includes(term) ||
      product.category?.toLowerCase().includes(term) ||
      product.unit?.toLowerCase().includes(term)
    );
  }, [products, search]);

  const getStockValue = (product, rateKey) => {
    const quantity = Number(product.quantity) || 0;
    const rate = Number(product[rateKey]) || 0;
    return quantity * rate;
  };

  const totalPurchaseValue = products.reduce((sum, product) => sum + getStockValue(product, "purchaseRate"), 0);
  const totalSellingValue = products.reduce((sum, product) => sum + getStockValue(product, "sellingRate"), 0);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const payload = {
        productName: formData.productName.trim(),
        nameSideText: formData.nameSideText.trim(),
        category: formData.category.trim(),
        quantity: Number(formData.quantity),
        unit: formData.unit,
        entryDate: formData.entryDate,
        purchaseRate: Number(formData.purchaseRate),
        sellingRate: Number(formData.sellingRate),
        currency: "BDT",
      };

      if (editingId) {
        await updateDoc(doc(db, "mbdsProducts", editingId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "mbdsProducts"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      setFormData({ ...emptyForm, entryDate: today() });
      setEditingId(null);
    } catch (error) {
      alert("MBDS product save failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setFormData({
      productName: product.productName || "",
      nameSideText: product.nameSideText || "",
      category: product.category || "",
      quantity: product.quantity ?? "",
      unit: product.unit || "pcs",
      entryDate: product.entryDate || today(),
      purchaseRate: product.purchaseRate ?? "",
      sellingRate: product.sellingRate ?? "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, entryDate: today() });
  };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(`Delete ${product.productName}?`);
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "mbdsProducts", product.id));
      if (editingId === product.id) {
        handleCancelEdit();
      }
    } catch (error) {
      alert("MBDS product delete failed: " + error.message);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ margin: 0, color: "#0f172a", fontSize: 18, fontWeight: 800 }}>{editingId ? "Edit Product" : "Add Product"}</h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>MBDS stock data will be saved in Firestore.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16 }}>
          <div>
            <label style={labelStyle}>Product Name *</label>
            <input required value={formData.productName} onChange={(e) => updateField("productName", e.target.value)} style={inputStyle} placeholder="Product name" />
          </div>
          <div>
            <label style={labelStyle}>Unit *</label>
            <select required value={formData.unit} onChange={(e) => updateField("unit", e.target.value)} style={inputStyle}>
              {UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Stock Quantity *</label>
            <input required type="number" min="0" step="0.01" value={formData.quantity} onChange={(e) => updateField("quantity", e.target.value)} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <label style={labelStyle}>Purchase Rate (BDT) *</label>
            <input required type="number" min="0" step="0.01" value={formData.purchaseRate} onChange={(e) => updateField("purchaseRate", e.target.value)} style={inputStyle} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Selling Rate (BDT) *</label>
            <input required type="number" min="0" step="0.01" value={formData.sellingRate} onChange={(e) => updateField("sellingRate", e.target.value)} style={inputStyle} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Product Category (Optional)</label>
            <input value={formData.category} onChange={(e) => updateField("category", e.target.value)} style={inputStyle} placeholder="Category" />
          </div>
          <div>
            <label style={labelStyle}>Name Side Text (Optional)</label>
            <input value={formData.nameSideText} onChange={(e) => updateField("nameSideText", e.target.value)} style={inputStyle} placeholder="e.g. Premium" />
          </div>
          <div>
            <label style={labelStyle}>Entry Date (Optional)</label>
            <input type="date" value={formData.entryDate} onChange={(e) => updateField("entryDate", e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
            {editingId && (
              <button type="button" onClick={handleCancelEdit} style={{ flex: 1, padding: "12px 16px", borderRadius: 8, border: "1px solid #cbd5e1", background: "white", color: "#475569", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
                Cancel
              </button>
            )}
            <button type="submit" disabled={loading} style={{ flex: 1, width: "100%", padding: "12px 16px", borderRadius: 8, border: "none", background: "#0B47B3", color: "white", fontSize: 14, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.75 : 1 }}>
              {loading ? "Saving..." : editingId ? "Update Product" : "Add Product"}
            </button>
          </div>
        </form>
      </div>

      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, color: "#0f172a", fontSize: 18, fontWeight: 800 }}>Product List</h3>
            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{filteredProducts.length} products found</p>
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, maxWidth: 260 }} placeholder="Search products..." />
        </div>

        <div className="table-responsive">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 950 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                {["Product Name", "Category", "Stock", "Entry Date", "Purchase Rate", "Selling Rate", "Stock Value", "Action"].map((heading) => (
                  <th key={heading} style={{ textAlign: "left", padding: "0 10px 12px", fontSize: 11, color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#0f172a", fontWeight: 800 }}>
                    <span>{product.productName}</span>
                    {product.nameSideText?.trim() && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "2px 8px", display: "inline-block" }}>
                        {product.nameSideText}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#475569", fontWeight: 600 }}>{product.category}</td>







                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#475569", fontWeight: 700 }}>{Number(product.quantity) || 0} {product.unit}</td>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#475569", fontWeight: 600 }}>{product.entryDate}</td>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#0f172a", fontWeight: 700 }}>{formatBDT(product.purchaseRate)}</td>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#0f172a", fontWeight: 700 }}>{formatBDT(product.sellingRate)}</td>
                  <td style={{ padding: "14px 10px", fontSize: 13, color: "#0f172a", fontWeight: 800 }}>{formatBDT(getStockValue(product, "purchaseRate"))}</td>
                  <td style={{ padding: "14px 10px" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => handleEdit(product)} style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#0B47B3", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Edit</button>
                      <button type="button" onClick={() => handleDelete(product)} style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredProducts.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "34px 0", margin: 0 }}>No MBDS products found.</p>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16 }}>
        {[
          { label: "Total Products", value: products.length, color: "#0B47B3", bg: "#dbeafe" },
          { label: "Stock Purchase Value", value: formatBDT(totalPurchaseValue), color: "#047857", bg: "#d1fae5" },
          { label: "Stock Selling Value", value: formatBDT(totalSellingValue), color: "#7c3aed", bg: "#ede9fe" },
        ].map((item) => (
          <div key={item.label} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>{item.label}</span>
              <span style={{ width: 34, height: 34, borderRadius: 8, background: item.bg, color: item.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
                {item.label.charAt(0)}
              </span>
            </div>
            <p style={{ margin: "12px 0 0", fontSize: 22, fontWeight: 800, color: "#0f172a", fontFamily: "system-ui, sans-serif" }}>{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}